import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { EbookHeaderCard } from '../components/EbookPromo'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { BlogTagChips } from '../components/BlogTagChips'
import { UpstreamUnavailablePanel } from '../components/UpstreamUnavailablePage'
import { BLOG_FALLBACK_IMAGE, formatPublishedDate, getStorefrontConfig, toExternalImage } from '../lib/dropify'
import type { StorefrontBlogPost } from '../lib/dropify'
import { listBlogPosts } from '../lib/blog'
import { DEFAULT_OG_IMAGE, absoluteUrl } from '../lib/seo'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'

const BLOG_CARD_IMAGE_TRANSFORM = { width: 700, height: 420, smart: true, format: 'webp', quality: 75 } as const

export const Route = createFileRoute('/blog/tag/$tag')({
  loader: async ({ params }) => {
    const [config, postsResult] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listBlogPosts({ data: { tag: params.tag, limit: 24 } })
        .then((posts) => ({ posts, unavailable: false }))
        .catch(() => ({ posts: [] as StorefrontBlogPost[], unavailable: true })),
    ])
    const posts = postsResult.posts

    // Recover the human-readable tag name from the posts when available so the
    // heading reads "grosir surabaya" rather than the slug "grosir-surabaya".
    const tagName =
      posts.flatMap((post) => post.tags).find((tag) => tag.slug === params.tag)?.name ??
      params.tag.replace(/-/g, ' ')

    return { config, posts, tagName, postsUnavailable: postsResult.unavailable }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.postsUnavailable ?? false),
  head: ({ loaderData, params }) => {
    const tagName = loaderData?.tagName ?? params.tag.replace(/-/g, ' ')
    const title = `Artikel: ${tagName} | Blog Dropify`
    const description = `Kumpulan artikel dan tips Dropify dengan topik "${tagName}". Informasi grosir terbaru dari distributor tangan pertama.`

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        // Tag archives are navigation aids. Individual articles and dedicated
        // commercial landing pages should own search queries instead.
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(`/blog/tag/${params.tag}`) },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl(`/blog/tag/${params.tag}`) },
      ],
    }
  },
  component: BlogTagPage,
})

function BlogCard({ post }: { post: StorefrontBlogPost }) {
  const publishedDate = formatPublishedDate(post.published_at)
  const postHref = `/blog/${post.slug || post.id}`

  return (
    <div className="group relative bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-200 flex flex-col">
      <a href={postHref} aria-label={post.title} className="absolute inset-0 z-0" />
      <div className="relative overflow-hidden" style={{ height: 200 }}>
        <img
          src={toExternalImage(post.featured_image_url, BLOG_CARD_IMAGE_TRANSFORM) || BLOG_FALLBACK_IMAGE}
          alt={post.featured_image_alt || post.title}
          width="700"
          height="420"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {publishedDate && (
          <div className="absolute top-4 left-4 bg-blue-600 text-white text-center px-2.5 py-2 rounded-lg leading-none">
            <span className="text-xs font-semibold uppercase block">{publishedDate}</span>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3 flex-wrap relative z-10">
          <BlogTagChips tags={post.tags} chipClassName="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" />
        </div>
        <h3 className="relative z-10 text-sm font-bold text-slate-800 leading-snug mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 flex-1">
          <a href={postHref} className="hover:underline">{post.title}</a>
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-4">{post.excerpt || 'Baca tips dan informasi seputar bisnis grosir dari Dropify.'}</p>
        <span className="text-xs font-bold text-blue-600 group-hover:text-blue-700 uppercase tracking-wider flex items-center gap-1 transition-colors">
          Baca Selengkapnya <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  )
}

function BlogTagPage() {
  const { posts, config, tagName, postsUnavailable } = Route.useLoaderData()
  const { tag } = Route.useParams()

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
                <a href="/" className="hover:text-white transition-colors">Beranda</a>
                <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                <a href="/blog" className="hover:text-white transition-colors">Blog</a>
                <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                <span className="text-white font-medium">{tagName}</span>
              </nav>
              <p className="text-blue-400 text-xs font-bold uppercase tracking-wider mb-2">Topik</p>
              <h1 className="text-4xl font-black text-white tracking-tight mb-2">{tagName}</h1>
              <p className="text-slate-300 text-sm max-w-lg">
                Semua artikel Dropify dengan topik "{tagName}".
              </p>
            </div>

            <EbookHeaderCard location="blog_tag" />
          </div>
        </div>
      </div>

      <WhatsAppChannelSection />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {postsUnavailable ? (
          <UpstreamUnavailablePanel
            path={`/blog/tag/${encodeURIComponent(tag)}`}
            title="Daftar artikel topik ini sedang disinkronkan"
            description="Coba muat ulang atau buka arsip blog untuk melihat panduan grosir lain yang sudah tersedia."
          />
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-slate-500">
            Belum ada artikel untuk topik ini.{' '}
            <a href="/blog" className="text-blue-600 font-semibold hover:underline">Lihat semua artikel</a>
          </div>
        )}
      </div>

      <Footer config={config} />
    </div>
  )
}
